import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { getCanvasElementTitle } from '@/utils/elementsUtils';
import { IndexDbService } from './indexDbService';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('DynamicVectorService', dynamicVectorService);

    const indexDbService = new IndexDbService();

    function dynamicVectorService(
        $rootScope,
        $timeout,
        $q,
        $mdDialog,
        blockUI,
        LibrariesAPIService,
        VectorUtilService,
        ElementsColorService
    ) {
        const skipSaveTestingValues = $rootScope?.user?.AccessRoles?.Serialized?.split(',')?.includes('skip-save-testing-values');

        async function changeDynamicEPSPosition(object, position, canvas) {
            if (typeof position === 'undefined') {
                return;
            }

            if (position < 1) {
                position = 1;
            }

            const EPSGroup = object;
            EPSGroup.selectedPosition = position;

            var vector;
            // If we have a vector library, use the positions from it
            if (EPSGroup.vectorLibraryId) {
                vector = await LibrariesAPIService.getLibraryElementAtPosition(
                    EPSGroup.vectorLibraryId,
                    EPSGroup.selectedPosition
                );
            } else {
                // Get the SVG string
                vector = _.find(EPSGroup.vectors, function (v) {
                    return v.position === position - 1;
                });
            }

            if (EPSGroup.contentIsPdf) {
                EPSGroup.set('pdf', EPSGroup.pdf);
                await EPSGroup._contentLoadPromise;
            } else if (vector) {
                const svgString = await getSvgStringFromPath(vector.svgPath);
                EPSGroup.set('svgString', svgString);
                await EPSGroup._contentLoadPromise;
                canvas.renderAll();
            } else {
                EPSGroup.set('svgString', EPSGroup.svgString ?? '');
                await EPSGroup._contentLoadPromise;
                canvas.renderAll();
                console.warn('No vector found at position: ' + (position - 1));
            }
        }

        async function changeDynamicEPSColorPosition(canvasObject, position, colorPicker, canvas) {
            await ElementsColorService.changeDynamicColorPosition(canvasObject, position, colorPicker, canvas);
        }

        async function saveDynamicEPSColor(color, canvasObject, editMode) {
            await ElementsColorService.saveDynamicColor(color, canvasObject, editMode);
        }

        function addEPSGroup(object, position, svgPath, epsPath, editMode) {
            const EPSGroup = object;

            // Check if we already have a vector in this position, if we do, overwrite it
            const vector = _.find(EPSGroup.vectors, (v) => {
                return v.position === position - 1;
            });

            if (vector) {
                console.log('Editing ' + svgPath + ' at ' + (position - 1));
                vector.svgPath = svgPath;
                vector.epsPath = epsPath;
                vector.position = position - 1;
            }
            // Otherwise, add the new position
            else {
                console.log('Adding ' + svgPath + ' at ' + (position - 1));
                EPSGroup.vectors.push({
                    svgPath: svgPath,
                    epsPath: epsPath,
                    position: position - 1
                });
            }

            if (editMode) {
                EPSGroup.setEditedProp('vectors');
            }
        }

        async function setVectorPlaceholderProperties(properties, canvas, previewMode, isStepTwo, pageEpsId = null) {
            // Sort the vector list by position
            properties.vectors.sort(function (a, b) {
                return a.position - b.position;
            });

            // Load the vector position and size
            const epsWidth = properties.width;
            const epsHeight = properties.height;
            const objectCenterX = properties.centerX;
            const objectCenterY = properties.centerY;

            const EPSGroup = new fabric.VectorPlaceholder({
                strokeDashArray: canvas.getDashedArray(),
                strokeWidth: previewMode ? 0 : canvas.getVisibleStrokeWidth(),
                width: epsWidth,
                height: epsHeight,
                mtr: true,
                selectedPosition: 1,
                selectedColorPosition: 1,
                vectors: properties.vectors || [],
                colors: properties.colors ? JSON.parse(JSON.stringify(properties.colors)) : [],
                vectorLibraryId: properties.vectorLibraryId,
                colorLibraryId: properties.colorLibraryId,
                applyColoring: properties.applyColoring,
                isSolidBackground: properties.isSolidBackground || false,
                skewX: properties.skewX,
                skewY: properties.skewY,
                lockUniScalingWithSkew: isStepTwo,
                lockSkewingX: !isStepTwo,
                lockSkewingY: !isStepTwo,

                id: properties.id,
                uuid: properties.uuid,

                coverMaskArea: properties.coverMaskArea || false,
                rotationStep: properties.rotationStep || 1,

                locked: properties.locked,
                visible: properties.visible,
                name: properties.name,
                preventEditMode: previewMode || (!properties.allowUserUpload && !properties.allowEdit),
                isPrintArea: properties.isPrintArea,
                isOverlay: properties.isOverlay,
                groupId: properties.groupId,
                starMapConfig: properties.starMapConfig,
                qrConfig: properties.qrConfig,
                contentIsPdf: properties.contentIsPdf,
                allowEdit: properties.allowEdit,
                pageEpsId,
                allowSwapLibraryPosition: properties.allowSwapLibraryPosition,
                initWidth: properties.initWidth,
                initHeight: properties.initHeight,
                initCenterX: properties.initCenterX,
                initCenterY: properties.initCenterY,
                initRotation: properties.initRotation,
                limitMovement: properties.limitMovement || false
            });

            if (properties.contentIsPdf) {
                EPSGroup.set('pdf', properties.pdf);
            } else if (properties.svgString) {
                EPSGroup.set('svgString', properties.svgString);
            }

            // Set position
            EPSGroup.set('originX', 'center');
            EPSGroup.set('originY', 'center');
            EPSGroup.set({
                left: objectCenterX,
                top: objectCenterY
            });

            // Properties
            EPSGroup.set('centeredRotation', true);
            EPSGroup.set('cornerSize', 0.015 * canvas.getArtworkWidth());
            EPSGroup.set('transparentCorners', false);
            EPSGroup.set('angle', properties.rotation);
            EPSGroup.set('zIndex', properties.zIndex);
            EPSGroup.set('allowUserUpload', properties.allowUserUpload);

            if (previewMode) {
                EPSGroup.lockMovementY = true;
                EPSGroup.lockMovementX = true;
                EPSGroup.lockScalingX = true;
                EPSGroup.lockScalingY = true;
                EPSGroup.lockRotation = true;
            }

            EPSGroup.setFlip(properties.flipX, properties.flipY);
            await changeDynamicEPSPosition(EPSGroup, 1, canvas);

            // Set the initial color position
            if (EPSGroup.applyColoring) {
                await changeDynamicEPSColorPosition(EPSGroup, EPSGroup.selectedColorPosition, null, canvas);
            }

            return EPSGroup;
        }

        function getSvgStringFromPath(svgPath) {
            return $q(async function (resolve, reject) {
                try {
                    let svgString;
                    /**
                     * Fetch the SVG file without cache. Due to the behaviour of S3 (with or without CloudFront) and Chrome, if we load
                     *  and SVG file from HTML (when previewing the SVG file in a library) and then the same file is loaded here, the cached
                     *  request doesn't contain the needed CORS headers so it fails. This is apparently intended behaviour:
                     *
                     *      https://bugs.chromium.org/p/chromium/issues/detail?id=260239
                     *      https://bugs.chromium.org/p/chromium/issues/detail?id=260239
                     *
                     * Here are presented some workarounds to this: https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req/856948#856948,
                     *  the best way would be to add a lambda@edge function but that costs more so at this time we are just ignoring cache.
                     *
                     * Another useful link: https://stackoverflow.com/questions/26352083/chrome-cors-cache-requesting-same-file-from-two-different-origins
                     */
                    // TODO: use lamda@edge to properly use cache and forget about this kind of errors
                    const fetchHeaders = new Headers();
                    fetchHeaders.append('pragma', 'no-cache');
                    fetchHeaders.append('cache-control', 'no-cache');
                    const fetchConfig = {
                        method: 'GET',
                        headers: fetchHeaders,
                        mode: 'cors'
                    };

                    // Try fetching locally
                    const response = await fetch(svgPath, fetchConfig);
                    if (response.ok) {
                        svgString = await response.text();
                    } else if (response.status === 404) {
                        // Fallback to CloudFront URL
                        const cloudFrontUrl = buildCloudFrontUrl(svgPath);
                        const fallbackResponse = await fetch(cloudFrontUrl, fetchConfig);
                        if (fallbackResponse.ok) {
                            svgString = await fallbackResponse.text();
                        } else {
                            console.error(`CloudFront fallback failed: ${fallbackResponse.status}`);
                            throw new Error('Could not fetch SVG from any source.');
                        }
                    } else {
                        console.error(`Unexpected error from ${svgPath}: ${response.status}`);
                        throw new Error('Unexpected error');
                    }
                    resolve(svgString);
                } catch (err) {
                    $mdDialog.show(
                        $mdDialog
                            .alert()
                            .parent(document.body)
                            .clickOutsideToClose(true)
                            .title('Error loading EPS')
                            .textContent('There was an error loading the SVG file')
                            .ok('OK!')
                    );

                    if ($('#vectorEPSLoader')[0]) {
                        $('#vectorEPSLoader')[0].value = null;
                    }
                    reject(err);
                }
            });
        }

        function uploadEPSFile(file, progressCallback, isSolidBg = false) {
            return $q(async function (resolve, reject) {
                if (file) {
                    const message = isSolidBg ? 'Creating Solid Background' : `Uploading EPS '${file.name}' `;
                    try {
                        blockUI.start(message);
                        console.log(message);

                        // Upload EPS file
                        const response = await VectorUtilService.uploadVectorToS3(
                            file,
                            isSolidBg ? 'vector-svg' : 'vector',
                            isSolidBg,
                            false,
                            (type, progress) => {
                                if (type === 'upload') {
                                    if (progressCallback) progressCallback(progress);
                                    blockUI.message(`${message} ${parseInt(progress)}%`);
                                    $timeout(function () {
                                        $rootScope.$apply();
                                    });
                                } else {
                                    blockUI.message('Processing');
                                }
                            }
                        );

                        console.log(`Success on ${message}`);
                        blockUI.stop();
                        resolve(response);
                    } catch (err) {
                        blockUI.stop();
                        reject(err);
                        $mdDialog.show(
                            $mdDialog
                                .alert()
                                .parent(document.body)
                                .clickOutsideToClose(true)
                                .title(`${isSolidBg ? 'Solid Background' : 'EPS'} Error`)
                                .textContent(`Error ${message}`)
                                .ok('OK!')
                        );
                    }
                } else {
                    reject('No file provided');
                }
            });
        }

        async function loadSVGStringOnPlaceholder(svgString, object, canvas) {
            try {
                object.set('svgString', svgString);
                // The control rectangle/init position are reapplied asynchronously once the SVG
                //  finishes loading (see fabric.VectorPlaceholder._set) - render before this
                //  resolves and the canvas paints the placeholder mid-reset (unscaled) instead of
                //  its saved position, with nothing repainting it correctly afterward.
                await object._contentLoadPromise;
                canvas.requestRenderAll();
                blockUI.stop();
            } catch (error) {
                console.error(error);
                blockUI.stop();
            }
        }

        function loadFileOnPlaceholder(file, object, canvas, isFromStep2 = false, firstStepPlaceholder = null) {
            return new Promise(async (resolve, reject) => {
                if (file) {
                    const extension = file.name.split('.').pop().toLowerCase();
                    object.contentIsPdf = false;

                    // SVG files can be applied directly
                    if (extension === 'svg') {
                        const fileReader = new FileReader();
                        fileReader.onload = async function (fileLoadedEvent) {
                            const svgString = fileLoadedEvent.target.result;
                            object.set('svgString', svgString);
                            if (!skipSaveTestingValues) {
                                indexDbService.findAndEditValue(object.uuid, svgString);
                            }
                            if (isFromStep2 && firstStepPlaceholder) firstStepPlaceholder.svgString = svgString;
                            // See the comment on loadSVGStringOnPlaceholder above - render only
                            //  after the async control-rectangle/init position reapply finishes.
                            await object._contentLoadPromise;
                            canvas.renderAll();
                            resolve();
                        };
                        fileReader.readAsText(file);
                    }
                    // EPS files must be converted to SVG
                    else if (extension === 'eps') {
                        try {
                            blockUI.start('Uploading EPS');
                            const response = await VectorUtilService.getSvgPathFromEpsFile(file, (type, progress) => {
                                if (type === 'upload') {
                                    blockUI.message(`Uploading EPS: ${parseInt(progress)}%`);
                                    $timeout(() => $rootScope.$apply());
                                } else {
                                    blockUI.message(`Processing`);
                                }
                            });
                            if (response && response.SVG) {
                                const svgString = await getSvgStringFromPath(response.SVG);
                                object.set('svgString', svgString);
                                if (!skipSaveTestingValues) {
                                    indexDbService.findAndEditValue(object.uuid, svgString);
                                }
                                if (isFromStep2 && firstStepPlaceholder) firstStepPlaceholder.svgString = svgString;
                                // See the comment on loadSVGStringOnPlaceholder above - render
                                //  only after the async control-rectangle/init position reapply
                                //  finishes.
                                await object._contentLoadPromise;
                                canvas.renderAll();
                                blockUI.stop();
                                resolve();
                            } else {
                                blockUI.stop();
                                console.error('No svg string present');
                                reject();
                            }
                        } catch (error) {
                            console.error(error);
                            blockUI.stop();
                            reject(error);
                        }
                    } else if (extension === 'pdf') {
                        object.set('pdf', file);
                        object.contentIsPdf = true;
                        resolve();
                    } else {
                        console.error('Invalid file extension: ' + extension);
                        reject('Invalid file extension: ' + extension);
                    }
                } else {
                    resolve();
                }
            });
        }

        function buildDynamicVectorProperties(parameters, override = {}) {
            const { canvas, canvasWidth, canvasHeight, id, uuid, name, zIndex, isSolidBackground } = parameters;
            return {
                width: 0.3 * canvasWidth,
                height: 0.3 * canvasHeight,
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                selectedPosition: 1,
                selectedColorPosition: 1,
                applyColoring: false,
                vectors: [],
                colors: [],
                lockSkewingX: true,
                lockSkewingY: true,
                id,
                uuid,
                isOverlay: false,
                isPrintArea: false,
                name,
                strokeWidth: canvas.getVisibleStrokeWidth(),
                strokeDashArray: canvas.getDashedArray(),
                pageEpsId: canvas.getFirstActivePageEpsId(),
                zIndex: zIndex,
                isSolidBackground,
                // Matches DynamicImageService.buildDynamicImageProperties(): a freshly-created
                //  dynamic vector defaults to allowEdit=false, so double-click editing must start
                //  disabled too - otherwise it stays enabled until the "Allow moving vector inside
                //  bounding box" checkbox is toggled at least once.
                allowEdit: false,
                preventEditMode: true,
                ...override
            };
        }

        function addDynamicVector(parameters, override = {}) {
            const { canvasWidth, zIndex } = parameters;
            const dynamicVectorProperties = buildDynamicVectorProperties(parameters, override);
            const vectorPlaceholder = new fabric.VectorPlaceholder(dynamicVectorProperties);

            // Properties
            vectorPlaceholder.set('zIndex', zIndex);
            vectorPlaceholder.set('centeredRotation', true);
            vectorPlaceholder.set('cornerSize', 0.015 * canvasWidth);
            vectorPlaceholder.set('transparentCorners', false);
            vectorPlaceholder.name = getCanvasElementTitle(vectorPlaceholder);
            return vectorPlaceholder;
        }

        return {
            uploadEPSFile: uploadEPSFile,
            setVectorPlaceholderProperties: setVectorPlaceholderProperties,
            changeDynamicEPSPosition: changeDynamicEPSPosition,
            changeDynamicEPSColorPosition: changeDynamicEPSColorPosition,
            addEPSGroup: addEPSGroup,
            saveDynamicEPSColor: saveDynamicEPSColor,
            loadFileOnPlaceholder: loadFileOnPlaceholder,
            loadSVGStringOnPlaceholder: loadSVGStringOnPlaceholder,
            addDynamicVector: addDynamicVector,
            buildDynamicVectorProperties: buildDynamicVectorProperties
        };
    }
})();
